import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { take } from 'rxjs/operators';


/**
 * Keeps focus outside content while ng-bootstrap applies aria-hidden to it.
 *
 * NgbModal hides the application root before its modal window has a chance to
 * receive focus. Blurring the launcher first prevents an aria-hidden ancestor
 * from retaining the focused element. Focus is restored after the modal has
 * completely closed, matching the accessibility behaviour users expect.
 */
@Injectable({
	providedIn: 'root'
})
export class NgModalFocusService {

	constructor(
		@Inject(DOCUMENT) private readonly document: Document
	) {}


	/**------------------------------------------------------
	 * Open a modal after safely handing focus off from its launcher.
	 */
	open(openModal: () => NgbModalRef): NgbModalRef {
		const triggerElement: HTMLElement | null = this.getFocusedElement();
		triggerElement?.blur();

		let modalRef: NgbModalRef;
		try {
			modalRef = openModal();
		} catch (error) {
			this.deferFocusRestore(triggerElement);
			throw error;
		}

		modalRef.hidden.pipe(take(1)).subscribe(() => {
			this.deferFocusRestore(triggerElement);
		});
		return modalRef;
	}


	/**------------------------------------------------------
	 * Focus Helpers
	 */
	private getFocusedElement(): HTMLElement | null {
		const activeElement: Element | null = this.document.activeElement;
		if (!activeElement || activeElement === this.document.body) return null;
		if (typeof (activeElement as HTMLElement).blur !== 'function') return null;

		return activeElement as HTMLElement;
	}

	private deferFocusRestore(triggerElement: HTMLElement | null): void {
		if (!triggerElement) return;

		// NgbModal also schedules focus restoration. Queue ours afterwards so the
		// original launcher is the final focused element with or without animation.
		setTimeout(() => {
			this.restoreFocus(triggerElement);
		}, 0);
	}

	private restoreFocus(triggerElement: HTMLElement): void {
		if (!triggerElement.isConnected || triggerElement.matches(':disabled')) return;
		if (triggerElement.closest('[aria-hidden="true"], [inert]')) return;

		// Do not move focus behind a newer modal. For nested modals, restoration is
		// allowed only when the launcher belongs to the currently visible dialog.
		const visibleModalWindows: NodeListOf<HTMLElement> = this.document.querySelectorAll<HTMLElement>('ngb-modal-window.show');
		const activeModalWindow: HTMLElement | null = visibleModalWindows.item(visibleModalWindows.length - 1);
		if (activeModalWindow && !activeModalWindow.contains(triggerElement)) return;

		try {
			triggerElement.focus({ preventScroll: true });
		} catch {
			triggerElement.focus();
		}
	}
}
